import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { APP_LISTING_KIND, eventToAppListing, type AppListing } from '@/lib/appTypes';

interface UseAppsOptions {
  category?: string;
  author?: string;
  limit?: number;
}

export function useApps(options: UseAppsOptions = {}) {
  const { nostr } = useNostr();
  const { category, author, limit = 100 } = options;

  return useQuery({
    queryKey: ['apps', category, author, limit],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);

      const filters: any = {
        kinds: [APP_LISTING_KIND],
        limit,
      };

      if (category) {
        filters['#t'] = [category];
      }

      if (author) {
        filters.authors = [author];
      }

      const events = await nostr.query([filters], { signal });

      // Convert events to AppListing objects and filter out invalid ones
      const apps = events
        .map(eventToAppListing)
        .filter((app): app is AppListing => app !== null)
        .sort((a, b) => b.createdAt - a.createdAt);

      return apps;
    },
  });
}

export function useApp(author: string, id: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['app', author, id],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(3000)]);

      const events = await nostr.query(
        [
          {
            kinds: [APP_LISTING_KIND],
            authors: [author],
            '#d': [id],
            limit: 1,
          },
        ],
        { signal }
      );

      if (events.length === 0) return null;

      return eventToAppListing(events[0]);
    },
    enabled: !!author && !!id,
  });
}
